import { useMemo, useState, type ReactNode } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import type { AiChart as AiChartData } from '@apis/aiAgent';
import { ExportSelectable, type IExportSelectionProps } from './ExportSelectable';
import styles from './AiChart.module.css';

// `ApexAxisChartSeries` is declared as a global ambient type by
// `apexcharts/types`, but the d-orca tsconfig doesn't pick that up
// automatically. Re-declare the small subset we need locally.
type ApexAxisChartSeries = Array<{ name?: string; data: (number | null)[] }>;

/**
 * AI-emitted chart rendered with ApexCharts. Mirrors Trusstor's
 * `AiAssistantPage/components/AiChart` — same chart-type switcher pill
 * in the top-right corner, same animation profile, same colour palette.
 *
 * Colour palette + axis colours are hard-coded as hex literals because
 * ApexCharts processes them as raw strings (it doesn't resolve CSS
 * custom properties). The palette mirrors Trusstor's $blue/$green/
 * $yellow/$red ramp; if the d-orca brand-blue tokens shift, update
 * `BRAND_BLUE` etc. here in lockstep.
 */

type ChartType = AiChartData['chartType'];

const BRAND_BLUE = '#2196f3';        // var(--brand-blue)
const BRAND_BLUE_LIGHT = '#64b5f6';  // var(--brand-blue-light)
const BRAND_BLUE_XLIGHT = '#90caf9'; // var(--brand-blue-xlight)
const BRAND_BLUE_DEEP = '#0d47a1';   // var(--brand-blue-deep)
const TITLE_COLOR = BRAND_BLUE_DEEP;
const AXIS_COLOR = '#6f7985';        // var(--g500)
const GRID_COLOR = '#e7e9eb';        // var(--g100)

const CHART_COLORS: string[] = [
  BRAND_BLUE,
  '#4caf50', // green400
  '#ca8a04', // yellow600
  '#dc2626', // red600
  BRAND_BLUE_XLIGHT,
  '#388e3c', // green600
  '#ffc107', // yellow
  '#f87171', // red400
  BRAND_BLUE_LIGHT,
  '#81c784', // green300
];

interface ChartTypeOption {
  type: ChartType;
  label: string;
  icon: ReactNode;
}

const ICON_PROPS = {
  width: 14,
  height: 14,
  viewBox: '0 0 16 16',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const BarIcon = () => (
  <svg {...ICON_PROPS}>
    <line x1="3" y1="13" x2="3" y2="8" />
    <line x1="7" y1="13" x2="7" y2="4" />
    <line x1="11" y1="13" x2="11" y2="6" />
    <line x1="2" y1="13.5" x2="14" y2="13.5" />
  </svg>
);

const LineIcon = () => (
  <svg {...ICON_PROPS}>
    <polyline points="2,12 6,7 9,10 14,3" />
    <line x1="2" y1="13.5" x2="14" y2="13.5" />
  </svg>
);

const AreaIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M2,12 L6,7 L9,10 L14,3 L14,13 L2,13 Z" fill="currentColor" fillOpacity="0.18" />
    <line x1="2" y1="13.5" x2="14" y2="13.5" />
  </svg>
);

const PieIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="8" cy="8" r="5.5" />
    <path d="M8,8 L8,2.5 A5.5,5.5 0 0 1 13.5,8 Z" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const DonutIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="8" cy="8" r="5.5" />
    <circle cx="8" cy="8" r="2.4" />
  </svg>
);

const ALL_OPTIONS: ChartTypeOption[] = [
  { type: 'bar', label: 'Bar', icon: <BarIcon /> },
  { type: 'line', label: 'Line', icon: <LineIcon /> },
  { type: 'area', label: 'Area', icon: <AreaIcon /> },
  { type: 'pie', label: 'Pie', icon: <PieIcon /> },
  { type: 'donut', label: 'Donut', icon: <DonutIcon /> },
];

interface IAiChartProps extends IExportSelectionProps {
  chart: AiChartData;
}

export function AiChart(props: IAiChartProps) {
  const { chart, ...selection } = props;
  const isMultiSeries = chart.series.length > 1;
  const hasGap = chart.series.some((s) => s.data.some((v) => v === null));
  const [selectedType, setSelectedType] = useState<ChartType>(chart.chartType);
  const [prevPropType, setPrevPropType] = useState<ChartType>(chart.chartType);

  // Sync local switcher state when the upstream chart_type prop changes
  // (e.g. a new conversation message arrives with a different default).
  if (prevPropType !== chart.chartType) {
    setPrevPropType(chart.chartType);
    setSelectedType(chart.chartType);
  }

  // Pie / donut only make sense with a single series — hide them from
  // the switcher when there are multiple series so the user doesn't
  // pick an incompatible type.
  const availableOptions = useMemo<ChartTypeOption[]>(() => {
    // A pie cannot show a gap: a null slice would be a zero, which says "none"
    // where the truth is "no rate". Only the axis charts can draw a gap.
    if (isMultiSeries || hasGap) return ALL_OPTIONS.filter((o) => o.type !== 'pie' && o.type !== 'donut');
    return ALL_OPTIONS;
  }, [isMultiSeries, hasGap]);

  const activeType = useMemo<ChartType>(() => {
    const isAvailable = availableOptions.some((o) => o.type === selectedType);
    return isAvailable ? selectedType : availableOptions[0]!.type;
  }, [availableOptions, selectedType]);

  const isPieType = activeType === 'pie' || activeType === 'donut';

  const series = useMemo<ApexAxisChartSeries | number[]>(() => {
    if (isPieType) {
      // Pie is only offered when there is no gap, so the filter drops nothing
      // in practice; it is what keeps the slice count honest if that changes.
      return (chart.series[0]?.data ?? []).filter((v): v is number => v !== null);
    }
    return chart.series.map((s) => ({ name: s.name, data: s.data }));
  }, [chart.series, isPieType]);

  const options = useMemo<ApexOptions>(() => {
    const base: ApexOptions = {
      colors: CHART_COLORS,
      chart: {
        toolbar: { show: false },
        fontFamily: 'inherit',
        background: 'transparent',
        // ApexOptions' generated types drop `easing` for some versions
        // even though the runtime supports it. Cast to any so the field
        // makes it into the runtime config.
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 350,
          animateGradually: { enabled: false, delay: 0 },
          dynamicAnimation: { enabled: true, speed: 350 },
        } as ApexOptions['chart'] extends infer T ? T extends { animations?: infer A } ? A : never : never,
      },
      title: {
        text: chart.title,
        style: { fontSize: '14px', fontWeight: 600, color: TITLE_COLOR },
      },
      legend: {
        position: 'bottom',
        fontSize: '12px',
        labels: { colors: TITLE_COLOR },
        onItemHover: { highlightDataSeries: true },
      },
      tooltip: { theme: 'light' },
    };

    if (isPieType) {
      return {
        ...base,
        labels: chart.labels,
        fill: { opacity: 1, type: 'solid' },
        stroke: { width: 2, colors: ['#ffffff'] },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${Math.round(val)}%`,
        },
      };
    }

    return {
      ...base,
      xaxis: {
        categories: chart.labels,
        labels: {
          style: { fontSize: '11px', colors: AXIS_COLOR },
          rotate: chart.labels.length > 6 ? -45 : 0,
          rotateAlways: chart.labels.length > 6,
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: '11px', colors: AXIS_COLOR },
          formatter: (val: number) => (Number.isInteger(val) ? String(val) : val.toFixed(1)),
        },
      },
      grid: { borderColor: GRID_COLOR, strokeDashArray: 4 },
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth',
        width: activeType === 'line' ? 3 : 2,
        colors: CHART_COLORS,
      },
      fill: { opacity: activeType === 'area' ? 0.3 : 1 },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
    };
  }, [chart, isPieType, activeType]);

  const handleTypeChange = (type: ChartType) => setSelectedType(type);

  return (
    <ExportSelectable {...selection}>
      <div className={styles.chartContainer}>
        <div className={styles.typeSwitcher} role="tablist" aria-label="Chart type">
          {availableOptions.map((option) => {
            const isActive = option.type === activeType;
            return (
              <button
                key={option.type}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={option.label}
                title={option.label}
                className={`${styles.typeButton} ${isActive ? styles.typeButtonActive : ''}`}
                onClick={() => handleTypeChange(option.type)}
              >
                {option.icon}
              </button>
            );
          })}
        </div>
        {/* `key={activeType}` forces a re-mount when the type changes —
            ApexCharts can't switch type in-place cleanly, so we let it
            re-animate from scratch. */}
        <Chart
          key={activeType}
          options={options}
          series={series}
          type={activeType}
          height={280}
          width="100%"
        />
      </div>
    </ExportSelectable>
  );
}
